import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import FiltersBar from './components/FiltersBar';
import ExportButtons from './components/ExportButtons';
import HelpButton from '../../help/HelpButton';

export default function OutboundCompliance() {
  const [range, setRange, apiParams] = useRangeState('last_7');
  const [filters, setFilters] = useState({});
  const [tab, setTab] = useState('per_agent');
  const merged = { ...apiParams, ...filters };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'outbound_compliance', merged],
    queryFn: () => reportsApi.outboundCompliance(merged),
    keepPreviousData: true,
  });

  const perAgent = data?.per_agent || [];
  const perCountry = data?.per_country || [];
  const denials = data?.denials || [];

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <FiltersBar value={filters} onChange={setFilters} show={['agent']} />

      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Outbound compliance</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons report="outbound-compliance" params={merged} disabled={isLoading} />
          <button onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {isError && (
        <div className="text-sm text-red-600 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      {data?.meta?.source && (
        <div className="text-xs text-surface-muted mb-2">
          Data source: <strong>{data.meta.source}</strong>. {data.meta.note}
        </div>
      )}

      <div className="flex gap-1 border-b border-surface-border dark:border-surface-dark-border mb-3">
        {[['per_agent', 'Per agent'], ['per_country', 'Per country'], ['denials', `Denials (${denials.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={'px-3 py-1.5 text-sm border-b-2 -mb-px ' +
              (tab === id ? 'border-continuum-500 text-continuum-700 dark:text-continuum-200 font-medium'
                          : 'border-transparent hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle')}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'per_agent' && (
        <ReportTable
          headers={['extension','agent_name','allowed','denied','attempts','allowed_rate_pct']}
          rows={perAgent}
          columnLabels={{
            extension: 'Ext', agent_name: 'Agent',
            allowed: 'Allowed', denied: 'Denied', attempts: 'Attempts',
            allowed_rate_pct: 'Allowed %',
          }}
          formatters={{ allowed_rate_pct: (v) => v == null ? '—' : `${v}%` }}
        />
      )}
      {tab === 'per_country' && (
        <ReportTable
          headers={['country_code','allowed','denied']}
          rows={perCountry}
          columnLabels={{ country_code: 'Country', allowed: 'Allowed', denied: 'Denied' }}
        />
      )}
      {tab === 'denials' && (
        <ReportTable
          headers={['occurred_at','actor_email','extension','dialed','destination_e164','country_code','profile','reason']}
          rows={denials}
          columnLabels={{
            occurred_at: 'When', actor_email: 'Agent (email)',
            extension: 'Ext', dialed: 'Dialed', destination_e164: 'E.164',
            country_code: 'CC', profile: 'Profile', reason: 'Reason',
          }}
        />
      )}
    </div>
  );
}
